// ═════════════════════════════════════════════════════════════════════════════
// Mack · OVERVIEW tab (build-09) — THE VIEW OVER THE OTHER EIGHT
//
// SOURCE: scratchpad/mack_design_refs/build-09_tab-01_overview.png
// Colours INHERIT build-01 tokens + C.teal. Nothing new.
//
// FIRST IN THE STRIP, BUILT LAST — deliberately. Overview is a view over the
// eight surfaces behind it, so building it earlier would have meant hardcoding
// what should be reads.
//
// THE GOVERNING CONSTRAINT: this tab computes almost nothing of its own. Every
// figure comes from the tab that owns it.
//
//   dominant stage · reactive sheets · cycles · sheet states  → Phases
//   capital distribution · instrument coverage                → Flow
//   environment and its macro/sentiment split                 → Calculator
//   next armed event, consensus, wakes                        → Calendar
//   live attributions                                         → Cascade's real chain
//   stage classification                                      → stages.ts
//
// Exactly ONE block of content is declared here: the Fleet card, which no other
// tab holds. Everything else is imported. See the report.
//
// THE NARRATIVE IS COMPOSED, NOT STORED. It reads back the derived values in
// prose — dominant stage, capital distribution, reactive sheets — so it cannot
// drift from the orbs above it. A stored paragraph would be the failure mode.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useMemo, useState, useEffect } from 'react';
import { C, MONO } from './tokens';
import { card, panelTitle, outlinePill, microLabel } from './primitives';
import { MACK_PHASE_SHEETS } from './PhasesTab';
import { MACK_ROTATION, MACK_FLOW_INSTRUMENTS } from './FlowTab';
import { MACK_CALC_SHEETS, MACK_CALC_ENVIRONMENTS } from './CalculatorTab';
import { MACK_CALENDAR_PLACEHOLDER, countdown } from './CalendarTab';
import { MACK_CASCADE_INSTRUMENTS, computeCascade } from './CascadeTab';
import { classifyStage } from './stages';
import { strongestBlock, blockBandThirds } from './blocks';

const monoSmall: React.CSSProperties = { fontSize: 11.5, fontFamily: MONO };

// A sheet is REACTIVE above this activation, per the orb's own sub-line.
const REACTIVE_THRESHOLD = 0.55;
// The activation chips strike through below this. The design's visible split
// sits between 0.20 (struck) and 0.41 (clear), so the boundary lies in that gap;
// 0.30 is the reading. FLAGGED — one sample cannot pin it exactly.
const DORMANT_STRIKE = 0.30;

// ── FLEET — the one genuinely new block on this tab ──────────────────────────
// No other Mack tab holds fleet state, so it is declared here rather than
// imported. from_design: transcribed verbatim from the Overview capture.
export interface MackFleetRow { name: string; state: string; healthy: boolean; starred?: boolean }

export const MACK_FLEET: MackFleetRow[] = [
  { name: 'INGEST',      state: 'hourly + push',  healthy: true },
  { name: 'CALC',        state: 'on change',      healthy: false },
  { name: 'LEDGER',      state: '2 at horizon',   healthy: false },
  { name: 'MACRO-WEIGH', state: 'reasoning seat', healthy: true, starred: true },
];

// ── DERIVATIONS ──────────────────────────────────────────────────────────────

export interface RegimeRead {
  dominantStage: string; stageCount: number; sheetCount: number;
  environmentLabel: string; macroWeight: number;
  capitalDistribution: string;
  reactiveSheets: string[];
  cyclesTurning: string[]; intactCount: number;
}

export function computeRegime(): RegimeRead {
  const sheets = MACK_PHASE_SHEETS;

  // Dominant stage — count the sheets, same rule Flow's rotation panel uses.
  const tally = new Map<string, number>();
  sheets.forEach(s => {
    const st = classifyStage(s.delta_growth, s.delta_inflation);
    if (st !== '—') tally.set(st, (tally.get(st) ?? 0) + 1);
  });
  let dominantStage = '', stageCount = 0;
  tally.forEach((n, st) => { if (n > stageCount) { stageCount = n; dominantStage = st; } });

  // Environment — the phase-implied environment of the first reactive sheet,
  // read off the Calculator, which owns the environment table.
  const firstReactive = sheets.find(s => s.reactive) ?? sheets[0];
  const calcSheet = MACK_CALC_SHEETS.find(c => c.sheet_code === firstReactive.sheet_code);
  const env = MACK_CALC_ENVIRONMENTS.find(e => e.key === calcSheet?.default_environment)
           ?? MACK_CALC_ENVIRONMENTS[0];

  // Capital distribution — the dominant stage's leading asset class, off Flow.
  const rot = MACK_ROTATION.find(r => r.stage === dominantStage);

  return {
    dominantStage, stageCount, sheetCount: sheets.length,
    environmentLabel: env.label, macroWeight: env.macro_weight,
    capitalDistribution: rot?.most ?? '—',
    reactiveSheets: sheets.filter(s => s.reactive).map(s => s.display_label),
    cyclesTurning: sheets.filter(s => s.cycle_state === 'turning').map(s => s.display_label),
    intactCount: sheets.filter(s => s.cycle_state === 'intact').length,
  };
}

// Strongest block and its banding both come from blocks.ts (build-10). Overview
// used to carry its own copy of the order, the derivation and the band.
const blockBand = (v: number) => blockBandThirds(v, C.green, C.amber, C.red);

// ── ORB ICONS ────────────────────────────────────────────────────────────────
const ORB_ICON: Record<string, React.ReactNode> = {
  stage: <path d="M2 11l4-4 3 3 5-6" />,
  env: <><circle cx="8" cy="8" r="6" /><path d="M5 8h6" /></>,
  capital: <><path d="M3 13V8" /><path d="M8 13V4" /><path d="M13 13V10" /></>,
  reactive: <><circle cx="8" cy="8" r="6" /><path d="M8 4.5V8l2.5 1.5" /></>,
  cycles: <><path d="M13 8a5 5 0 1 1-1.6-3.7" /><path d="M13 3v3h-3" /></>,
};

function Orb({ icon, label, value, colour, sub }: {
  icon: string; label: string; value: React.ReactNode; colour: string; sub: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        border: `1px solid ${C.amberDim}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"
             fill="none" stroke={C.amber} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
          {ORB_ICON[icon]}
        </svg>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={microLabel}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: colour, marginTop: 5 }}>{value}</div>
        <div style={{ ...monoSmall, fontSize: 10.5, color: C.textMuted, marginTop: 4 }}>{sub}</div>
      </div>
    </div>
  );
}

// Measured (content 1507px ≈ 1:1 with the 1480 layout).
const ATTR_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '30px 120px minmax(0,1fr) 78px 108px 56px 96px 150px',
  alignItems: 'center', gap: 8,
};
const SUMMARY_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '96px minmax(0,1fr) 70px', alignItems: 'center',
};

// ── TAB ──────────────────────────────────────────────────────────────────────
//
// DISPLAY ONLY. The design shows no chip, selector, caret or link on this tab —
// it is a homepage, not a working surface. Nothing here is interactive and
// nothing was invented to make it so.

export function OverviewTab() {
  const r = useMemo(() => computeRegime(), []);

  // Live countdown on the next armed event, same 30s tick Calendar uses.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const sheetLabel = (code: string) =>
    MACK_PHASE_SHEETS.find(s => s.sheet_code === code)?.display_label ?? code;

  // The next armed event, straight off Calendar's data and its alert state.
  const nextEvent = useMemo(
    () => MACK_CALENDAR_PLACEHOLDER.scheduled.find(e => e.alert_set),
    [],
  );

  // THE REAL CHAIN, per instrument — not held results. Ranked by |W| of the
  // leading tier, top five, exactly as the design's pill declares.
  const attributions = useMemo(
    () => MACK_CASCADE_INSTRUMENTS
      .map(i => ({ inst: i, res: computeCascade(i) }))
      .sort((a, b) => Math.abs(b.res.weighted[0].W) - Math.abs(a.res.weighted[0].W))
      .slice(0, 5),
    [],
  );

  const confColour = (b: string) =>
    b === 'High' ? C.green : b === 'Moderate' ? C.amber : b === 'Low' ? C.amberState : C.red;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>

      {/* ── REGIME READ + NEXT ARMED EVENT — measured 894 / 596 ───────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '18px 22px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ ...panelTitle, flex: 1 }}>Regime read</div>
            <span style={{ ...outlinePill(C.amber), display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              Macro-weigh ·
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, display: 'inline-block' }} />
                Live
              </span>
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 18, marginTop: 18 }}>
            <Orb icon="stage" label="Dominant stage" colour={C.text}
                 value={r.dominantStage} sub={`${r.stageCount} of ${r.sheetCount} sheets`} />
            <Orb icon="env" label="Environment" colour={C.text}
                 value={r.environmentLabel}
                 sub={`macro ${r.macroWeight.toFixed(2)} · sentiment ${(1 - r.macroWeight).toFixed(2)}`} />
            <Orb icon="capital" label="Capital distribution" colour={C.amber}
                 value={r.capitalDistribution} sub="from stage rotation" />
            <Orb icon="reactive" label="Reactive sheets" colour={C.teal}
                 value={r.reactiveSheets.join(' · ')} sub={`A > ${REACTIVE_THRESHOLD} or at transition`} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 18, marginTop: 16 }}>
            <Orb icon="cycles" label="Cycles turning" colour={C.violet}
                 value={r.cyclesTurning.join(' · ') || '—'}
                 sub={`${r.intactCount} of ${r.sheetCount} intact`} />
          </div>

          {/* COMPOSED from the orbs above — never a stored paragraph. */}
          <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.7, marginTop: 20 }}>
            {r.dominantStage} dominates the board, which puts volume in{' '}
            {r.capitalDistribution.toLowerCase()} — anything expressed elsewhere trades in a
            thinner book.{' '}
            <span style={{ color: C.teal }}>{r.reactiveSheets.join(' and ')}</span> are reactive;
            everything else decays toward dormant on the calendar.
          </div>
        </div>

        <div style={{ ...card, padding: '18px 22px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ ...panelTitle, flex: 1 }}>Next armed event</div>
            <span style={{ ...outlinePill(C.red), display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.red, display: 'inline-block' }} />
              Alert set
            </span>
          </div>

          {nextEvent ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginTop: 17 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  border: `1px solid ${C.amberDim}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"
                       fill="none" stroke={C.amber} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
                    <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" />
                  </svg>
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>
                  {nextEvent.name} <span style={{ color: C.textMuted }}>·</span>{' '}
                  <span style={{ color: C.amber, fontFamily: MONO }}>{countdown(nextEvent.starts_at, now)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', marginTop: 12, borderTop: `1px solid ${C.rule}` }}>
                <span style={{ flex: 1, fontSize: 13, color: C.textSub }}>Consensus</span>
                <span style={{ ...monoSmall, color: C.textMuted }}>{nextEvent.consensus}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderTop: `1px solid ${C.rule}` }}>
                <span style={{ flex: 1, fontSize: 13, color: C.textSub }}>Wakes</span>
                <span style={{
                  fontSize: 10, fontFamily: MONO, color: C.violet,
                  border: `1px solid ${C.violetBd}`, borderRadius: 5, padding: '4px 9px',
                  textTransform: 'uppercase', letterSpacing: '0.1em',
                }}>
                  {sheetLabel(nextEvent.wakes_sheet)} · {nextEvent.wakes_block}
                </span>
              </div>

              <div style={{ ...microLabel, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.rule}` }}>
                Activation on landing — A resets to 1.00
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}>
                {MACK_PHASE_SHEETS.filter(s => s.sheet_code !== 'BLOC').map(s => {
                  const dormant = s.activation < DORMANT_STRIKE;
                  return (
                    <span key={s.sheet_code} style={{
                      fontSize: 9.5, fontFamily: MONO, borderRadius: 4, padding: '4px 8px',
                      border: `1px solid ${dormant ? C.red : C.green}`,
                      color: dormant ? C.red : C.green,
                      textDecoration: dormant ? 'line-through' : 'none',
                      whiteSpace: 'nowrap',
                    }}>
                      {s.display_label} · A {s.activation.toFixed(2)}
                    </span>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ ...monoSmall, color: C.textMuted, marginTop: 16 }}>No armed event.</div>
          )}
        </div>
      </div>

      {/* ── LIVE ATTRIBUTIONS — Cascade's real chain, per instrument ──── */}
      <div style={{ ...card, padding: '18px 30px 20px', overflowX: 'auto' }}>
        <div style={{ minWidth: 1150 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ ...panelTitle, flex: 1 }}>Live attributions — computed from the chain</div>
            <span style={{ ...outlinePill(C.violet), flexShrink: 0 }}>Ranked by |W|</span>
          </div>

          <div style={{ marginTop: 15 }}>
            {attributions.map(({ inst, res }, n) => {
              const top = res.weighted[0];
              return (
                <div key={inst.instrument} style={{
                  ...ATTR_GRID, minHeight: 48,
                  borderBottom: n === attributions.length - 1 ? 'none' : `1px solid ${C.rule}`,
                }}>
                  <span style={{ ...monoSmall, fontSize: 10.5, color: C.textMuted }}>{n + 1}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{inst.instrument}</span>
                  <span style={{ fontSize: 13, minWidth: 0 }}>
                    <span style={{ color: C.amber, fontWeight: 700 }}>{top.tier}</span>
                    <span style={{ color: C.textMuted }}>
                      {' '}{top.sheet_label.replace(' −', '')} — {res.dominantBlock} dominant
                    </span>
                  </span>
                  <span style={{
                    fontSize: 9, fontFamily: MONO, letterSpacing: '0.1em', textTransform: 'uppercase',
                    border: `1px solid ${C.teal}`, color: C.teal, borderRadius: 20,
                    padding: '3px 8px', textAlign: 'center', whiteSpace: 'nowrap',
                  }}>{res.speed.split(' ')[0]}</span>
                  <span style={{ ...monoSmall, color: C.textMuted }}>{res.reviewPoint}</span>
                  <span style={{ ...monoSmall, color: inst.new_attribution ? C.violet : C.textMuted }}>
                    {inst.new_attribution ? 'new' : 'cont'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: confColour(res.confidence) }}>
                    {res.confidence}
                  </span>
                  <span style={{ fontSize: 13, color: C.teal, textAlign: 'right' }}>{res.impliedSide}</span>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 12, fontStyle: 'italic', color: C.textMuted, marginTop: 16, lineHeight: 1.6 }}>
            Every row derives from the floor vectors, activation state, capacity gate and block
            routing behind it. Change a criterion on the Calculator and these move.
          </div>
        </div>
      </div>

      {/* ── FOUR SUMMARY CARDS ───────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '18px 20px 16px' }}>
          <div style={panelTitle}>Country sheets</div>
          <div style={{ marginTop: 14 }}>
            {MACK_PHASE_SHEETS.map(s => {
              const phase = classifyStage(s.delta_growth, s.delta_inflation);
              return (
                <div key={s.sheet_code} style={{ ...SUMMARY_GRID, minHeight: 37, borderTop: `1px solid ${C.rule}` }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{s.display_label}</span>
                  <span style={{ fontSize: 12.5, color: C.textMuted }}>{phase}</span>
                  <span style={{
                    ...monoSmall, textAlign: 'right',
                    color: s.activation > REACTIVE_THRESHOLD ? C.green : C.textMuted,
                  }}>{s.activation.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ ...card, padding: '18px 20px 16px' }}>
          <div style={panelTitle}>Strongest blocks</div>
          <div style={{ marginTop: 14 }}>
            {MACK_PHASE_SHEETS.map(s => {
              const k = strongestBlock(s.block_vector);
              const b = { key: k, value: s.block_vector[k] ?? 0 };
              return (
                <div key={s.sheet_code} style={{ ...SUMMARY_GRID, minHeight: 37, borderTop: `1px solid ${C.rule}` }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{s.display_label}</span>
                  <span style={{ fontSize: 12.5, color: C.textMuted }}>{b.key}</span>
                  <span style={{ ...monoSmall, textAlign: 'right', color: blockBand(b.value) }}>
                    {b.value.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ ...card, padding: '18px 20px 16px' }}>
          <div style={panelTitle}>Instrument coverage</div>
          <div style={{ marginTop: 14 }}>
            {(['FX', 'Commodity', 'Index'] as const).map(k => {
              const n = MACK_FLOW_INSTRUMENTS.filter(i => i.asset_class === k).length;
              return (
                <div key={k} style={{
                  display: 'flex', alignItems: 'center', minHeight: 37, borderTop: `1px solid ${C.rule}`,
                }}>
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: C.text }}>{k}</span>
                  <span style={{ ...monoSmall, color: C.amber }}>{n} instruments</span>
                </div>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 37, borderTop: `1px solid ${C.rule}` }}>
              <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: C.text }}>Sheets</span>
              <span style={{ ...monoSmall, color: C.amber }}>{MACK_PHASE_SHEETS.length} live</span>
            </div>
          </div>
        </div>

        <div style={{ ...card, padding: '18px 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ ...panelTitle, flex: 1 }}>Fleet</div>
            <span style={{ ...outlinePill(C.amber), fontSize: 9.5, padding: '4px 10px', flexShrink: 0 }}>
              3 + seat
            </span>
          </div>
          <div style={{ marginTop: 14 }}>
            {MACK_FLEET.map(f => (
              <div key={f.name} style={{
                display: 'flex', alignItems: 'center', gap: 10, minHeight: 37,
                borderTop: `1px solid ${C.rule}`,
              }}>
                <span style={{ flex: 1, ...monoSmall, color: C.text, letterSpacing: '0.06em' }}>
                  {f.name}{f.starred ? ' ★' : ''}
                </span>
                <span style={{ ...monoSmall, color: C.textMuted }}>{f.state}</span>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: f.healthy ? C.green : C.amber,
                }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
